// Color math for the whole site: parsing, the sRGB transfer curve, WCAG contrast, and OKLab.

import type { Vec3 } from "./vec.js";

const RGB_RE = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/;
const HEX_RE = /^#([0-9a-fA-F]{6})$/;

/** Parses a `#rrggbb` or `rgb(r,g,b)` color into 0 to 255 channels. */
export function parseColorChannels(color: string): Vec3 {
  const hex = HEX_RE.exec(color);
  if (hex) {
    const h = hex[1] ?? "";
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const rgb = RGB_RE.exec(color);
  if (rgb) {
    return [Number(rgb[1] ?? 0), Number(rgb[2] ?? 0), Number(rgb[3] ?? 0)];
  }
  throw new Error(
    `parseColorChannels: unrecognized color format (expected "#rrggbb" or "rgb(r,g,b)"): ${color}`,
  );
}

/** A `#rrggbb` or `rgb(r,g,b)` color as sRGB from 0 to 1. */
export function parseSrgb(color: string): Vec3 {
  const [r, g, b] = parseColorChannels(color);
  return [r / 255, g / 255, b / 255];
}

/** An sRGB channel (0 to 1) as linear light. */
function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Linear light as an sRGB channel (0 to 1). */
function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.max(c, 0) ** (1 / 2.4) - 0.055;
}

// sRGB to OKLab and back (Ottosson, https://bottosson.github.io/posts/oklab/). The field's
// shader has a GLSL copy of `oklabToSrgb` (field-shaders.ts).
export function srgbToOklab([r, g, b]: Vec3): Vec3 {
  const [lr, lg, lb] = [srgbToLinear(r), srgbToLinear(g), srgbToLinear(b)];
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

/** An OKLab color as sRGB from 0 to 1, not clamped. */
export function oklabToSrgb([lightness, a, b]: Vec3): Vec3 {
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    linearToSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    linearToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    linearToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  ];
}

/** The WCAG relative luminance of an sRGB color (0 to 255 channels), in [0, 1]. Unlike
    `relLuma` (Rec.709 luma on the gamma-encoded channels, for sorting palettes), this
    linearizes each channel first, which a correct contrast ratio needs. */
export function srgbLuminance(rgb: Vec3): number {
  const lin = (c: number): number => srgbToLinear(c / 255);
  return 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2]);
}

/** The WCAG 2.x contrast ratio between two sRGB colors (0 to 255 channels), in [1, 21]. */
export function contrastRatio(a: Vec3, b: Vec3): number {
  const la = srgbLuminance(a);
  const lb = srgbLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}
